import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, MessageCircle, Minus, Plus } from "lucide-react";
import InternalNav from "@/components/InternalNav";
import { useContent } from "@/lib/ContentProvider";

export default function Tarifs() {
  const { pricing: PRICING, contact: CONTACT } = useContent();
  const BASE = PRICING.baseNight;
  const BASE_P = PRICING.basePeople;
  const EXTRA = PRICING.extraPerson;
  const BAIN = PRICING.options.bainDeFleurs;
  const DEJ = PRICING.options.petitDejeuner;
  const DECOR = PRICING.options.decors;
  const WK_NIGHTS = PRICING.weekendNights;
  const MAX_PEOPLE = PRICING.maxPeople;
  // Taxe de séjour — logique Lodgify / TCO (5 % plafonné · adultes uniquement)
  const TAX_RATE = PRICING.cityTax?.rate ?? 0.05;
  const TAX_CAP = PRICING.cityTax?.perPersonPerNightCap ?? 2.30;

  const [type, setType] = useState("semaine");
  const [nights, setNights] = useState(1);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [bain, setBain] = useState(false);
  const [dej, setDej] = useState(false);
  const [decor, setDecor] = useState(false);
  const wk = type === "weekend";
  const n = wk ? WK_NIGHTS : nights;
  const people = adults + children;                    // nombre total d'occupants
  const remainingSlots = Math.max(0, MAX_PEOPLE - people);
  const fmt = (v) => (Math.round(v * 100) / 100).toFixed(2).replace(".", ",");

  const breakdown = useMemo(() => {
    // Hébergement — même règle qu'avant, l'extraPerson est déclenché à partir de basePeople
    // (adultes + enfants comptent tous comme des voyageurs pour le tarif nuit).
    const housing = BASE * n + Math.max(0, people - BASE_P) * EXTRA * n;
    // Petit-déjeuner — UN SEUL service à 25€ × nombre total de voyageurs, jamais × nuits
    const bfast = dej ? DEJ * people : 0;
    const bainAmt = bain ? BAIN : 0;
    const decorAmt = decor ? DECOR : 0;
    // Taxe de séjour — TCO : (hébergement ÷ nuits ÷ occupants) × rate, plafonné, × adultes × nuits
    // Extras exclus de la base. Enfants comptés dans le diviseur mais exonérés.
    const pricePerOccupantPerNight = people > 0 && n > 0 ? housing / n / people : 0;
    const taxUnit = Math.min(pricePerOccupantPerNight * TAX_RATE, TAX_CAP);
    const cityTax = taxUnit * adults * n;
    const total = housing + bfast + bainAmt + decorAmt + cityTax;
    return { housing, bfast, bainAmt, decorAmt, cityTax, taxUnit, total };
  }, [BASE, BASE_P, EXTRA, BAIN, DEJ, DECOR, TAX_RATE, TAX_CAP, n, people, adults, bain, dej, decor]);

  const total = breakdown.total;

  return (
    <div data-testid="tarifs-page" className="bg-refuge-bg min-h-screen">
      <InternalNav />

      <section className="pt-32 md:pt-40 pb-10 md:pb-14 bg-refuge-bg">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby mb-4">Repères tarifaires</p>
          <h1 className="font-heading text-4xl md:text-6xl font-light text-refuge-text mb-2 leading-[1.05]">
            Estimer <span className="italic">mon séjour</span>
          </h1>
        </div>
      </section>

      <section className="pb-20 md:pb-24 bg-refuge-bg">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-7">
              <div className="mb-8">
                <p className="font-heading text-lg mb-3">Type de séjour</p>
                <div className="grid grid-cols-2 gap-3" data-testid="stay-type-toggle">
                  {[{id:"semaine",l:"Semaine"},{id:"weekend",l:"Week-end"}].map((o)=>(
                    <button key={o.id} type="button" data-testid={`stay-type-${o.id}`} onClick={()=>setType(o.id)} className={`py-4 px-5 transition-all text-left border ${type===o.id?"border-refuge-ruby bg-refuge-ruby/5 text-refuge-ruby":"border-refuge-border bg-white text-refuge-text"}`}><span className="font-heading text-xl">{o.l}</span></button>
                  ))}
                </div>
                <div data-testid="stay-type-info" className="mt-3 flex items-start gap-2 px-4 py-3 rounded-lg bg-refuge-rose/10 border border-refuge-rose/30">
                  <span className="mt-[6px] w-1.5 h-1.5 rounded-full bg-refuge-ruby shrink-0" />
                  <p className="font-body text-xs md:text-sm text-refuge-soft leading-relaxed">
                    Une nuit est possible en semaine selon les périodes disponibles. Les séjours du week-end sont proposés avec un minimum de deux nuits.
                  </p>
                </div>
              </div>

              {!wk && (
                <div className="mb-8">
                  <p className="font-heading text-lg mb-3">Nombre de nuits <span className="font-body text-xs tracking-[0.18em] uppercase text-refuge-soft ml-2">en semaine</span></p>
                  <div className="flex items-center gap-4" data-testid="nights-counter">
                    <button data-testid="nights-minus" onClick={()=>setNights(Math.max(1,nights-1))} className="w-12 h-12 rounded-full border border-refuge-border flex items-center justify-center"><Minus size={16} /></button>
                    <span className="font-heading text-4xl w-16 text-center">{nights}</span>
                    <button data-testid="nights-plus" onClick={()=>setNights(Math.min(14,nights+1))} className="w-12 h-12 rounded-full border border-refuge-border flex items-center justify-center"><Plus size={16} /></button>
                  </div>
                </div>
              )}

              <div className="mb-8">
                <p className="font-heading text-lg mb-3">
                  Voyageurs
                  <span className="font-body text-xs tracking-[0.18em] uppercase text-refuge-soft ml-2">max {MAX_PEOPLE} occupants</span>
                </p>

                {/* Adultes */}
                <div className="flex items-center justify-between gap-4 py-3" data-testid="adults-counter">
                  <div>
                    <p className="font-heading text-base text-refuge-text">Adultes</p>
                    <p className="font-body text-xs text-refuge-soft">assujettis à la taxe de séjour</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <button data-testid="adults-minus" onClick={()=>setAdults(Math.max(1,adults-1))} className="w-11 h-11 rounded-full border border-refuge-border flex items-center justify-center"><Minus size={16} /></button>
                    <span className="font-heading text-3xl w-10 text-center">{adults}</span>
                    <button
                      data-testid="adults-plus"
                      onClick={()=>{ if (remainingSlots > 0) setAdults(adults+1); }}
                      disabled={remainingSlots === 0}
                      className={`w-11 h-11 rounded-full border flex items-center justify-center ${remainingSlots === 0 ? "border-refuge-border/40 text-refuge-soft/40 cursor-not-allowed" : "border-refuge-border"}`}
                    ><Plus size={16} /></button>
                  </div>
                </div>

                {/* Enfants */}
                <div className="flex items-center justify-between gap-4 py-3 border-t border-refuge-border/60" data-testid="children-counter">
                  <div>
                    <p className="font-heading text-base text-refuge-text">Enfants</p>
                    <p className="font-body text-xs text-refuge-soft">moins de 18 ans · exonérés de taxe de séjour</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <button data-testid="children-minus" onClick={()=>setChildren(Math.max(0,children-1))} className="w-11 h-11 rounded-full border border-refuge-border flex items-center justify-center"><Minus size={16} /></button>
                    <span className="font-heading text-3xl w-10 text-center">{children}</span>
                    <button
                      data-testid="children-plus"
                      onClick={()=>{ if (remainingSlots > 0) setChildren(children+1); }}
                      disabled={remainingSlots === 0}
                      className={`w-11 h-11 rounded-full border flex items-center justify-center ${remainingSlots === 0 ? "border-refuge-border/40 text-refuge-soft/40 cursor-not-allowed" : "border-refuge-border"}`}
                    ><Plus size={16} /></button>
                  </div>
                </div>
              </div>

              {[
                {id:"bain",state:bain,set:setBain,t:"Bain de fleurs",n:`rituel sensoriel · +${PRICING.options.bainDeFleurs}€`},
                {id:"dej",state:dej,set:setDej,t:"Petit déjeuner",n:`+${PRICING.options.petitDejeuner}€ / personne · un seul service`},
                {id:"decor",state:decor,set:setDecor,t:"Décoration du refuge",n:`mise en scène · +${PRICING.options.decors}€`},
              ].map((o)=>(
                <div key={o.id} className={`mb-4 p-5 flex items-center justify-between gap-6 border ${o.state?"border-refuge-ruby bg-refuge-ruby/5":"border-refuge-border bg-white"}`}>
                  <div><p className="font-heading text-xl">{o.t}</p><p className="font-body text-sm text-refuge-soft mt-1">{o.n}</p></div>
                  <div className="flex gap-2">
                    {[{v:false,l:"Non"},{v:true,l:"Oui"}].map((b)=>(
                      <button key={b.l} data-testid={`option-${o.id}-${b.l.toLowerCase()}`} onClick={()=>o.set(b.v)} className={`px-5 py-2 text-sm rounded-full border ${o.state===b.v?"bg-refuge-ruby border-refuge-ruby text-white":"border-refuge-border text-refuge-text"}`}>{b.l}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-32 p-7 md:p-9 bg-refuge-text text-white" data-testid="estimation-summary">
                <p className="font-body text-xs tracking-[0.2em] uppercase text-refuge-rose mb-4">Votre séjour comprend</p>
                <ul className="space-y-2 mb-6 text-sm text-white/85">
                  <li>
                    {n} {n>1?"nuits":"nuit"} au refuge · {adults} adulte{adults>1?"s":""}
                    {children>0 ? ` + ${children} enfant${children>1?"s":""}` : ""}
                  </li>
                  {bain && <li>Bain de fleurs · rituel sensoriel</li>}
                  {dej && <li>Petit déjeuner · un service pour {people} {people>1?"personnes":"personne"}</li>}
                  {decor && <li>Décoration du refuge · mise en scène</li>}
                </ul>

                {/* Détail ligne par ligne */}
                <div className="pt-5 border-t border-white/15 space-y-2 text-sm mb-4" data-testid="estimation-breakdown">
                  <div className="flex justify-between text-white/90">
                    <span>Hébergement</span><span data-testid="line-housing">{fmt(breakdown.housing)} €</span>
                  </div>
                  {bain && (
                    <div className="flex justify-between text-white/90"><span>Bain fleuri</span><span data-testid="line-bain">{fmt(breakdown.bainAmt)} €</span></div>
                  )}
                  {dej && (
                    <div className="flex justify-between text-white/90"><span>Petit-déjeuner</span><span data-testid="line-dej">{fmt(breakdown.bfast)} €</span></div>
                  )}
                  {decor && (
                    <div className="flex justify-between text-white/90"><span>Décor du refuge</span><span data-testid="line-decor">{fmt(breakdown.decorAmt)} €</span></div>
                  )}
                  <div className="flex justify-between text-white/70 text-xs italic pt-1">
                    <span>Taxe de séjour · {adults} adulte{adults>1?"s":""} × {n} nuit{n>1?"s":""}</span>
                    <span data-testid="line-city-tax">{fmt(breakdown.cityTax)} €</span>
                  </div>
                </div>

                <div className="pt-5 border-t border-white/20">
                  <p className="font-body text-xs tracking-[0.22em] uppercase text-refuge-rose mb-2">Total estimé</p>
                  <p className="font-heading text-5xl md:text-6xl mb-4" data-testid="estimation-total">{fmt(total)} €</p>
                  <p className="font-body text-xs text-white/55 leading-relaxed">
                    Taxe de séjour calculée selon la réglementation Lodgify · TCO 5 % plafonné à {fmt(TAX_CAP)} € / adulte / nuit. Reversée intégralement à la collectivité.
                  </p>
                </div>
                <div className="mt-8 flex flex-col gap-3">
                  <Link to="/reserver" data-testid="tarifs-cta-reserver" className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white text-refuge-ruby font-body text-sm">Réserver les dates <ArrowUpRight size={16} /></Link>
                  <a href={`https://wa.me/${CONTACT.whatsappNumber}`} target="_blank" rel="noopener noreferrer" data-testid="tarifs-cta-whatsapp" className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full border border-white/50 text-white font-body text-sm"><MessageCircle size={16} /> J'ai une question</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-refuge-cta py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-6 md:gap-8">
          <div className="p-9 bg-white rounded-2xl border border-refuge-border" data-testid="card-semaine">
            <p className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby mb-4">En semaine</p>
            <h3 className="font-heading text-3xl font-light text-refuge-text mb-6">Une nuit <span className="italic">au refuge</span></h3>
            <p className="font-heading text-6xl text-refuge-ruby mb-2">≈ 150€</p>
            <p className="font-body text-sm text-refuge-soft mb-6">la nuit · pour 2 personnes</p>
            <p className="font-body text-sm text-refuge-soft/85 pt-5 border-t border-refuge-border">+15€ / voyageur supplémentaire</p>
          </div>
          <div className="p-9 rounded-2xl relative bg-refuge-ruby text-white" data-testid="card-weekend">
            <span className="absolute top-6 right-6 font-body text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 bg-white text-refuge-ruby rounded">Favori</span>
            <p className="font-body text-xs tracking-[0.2em] uppercase text-refuge-rose mb-4">Week-end</p>
            <h3 className="font-heading text-3xl font-light mb-6">Séjour <span className="italic text-refuge-rose">week-end</span></h3>
            <p className="font-heading text-6xl mb-2">≈ 300€</p>
            <p className="font-body text-sm text-white/70 mb-6">pour 2 nuits · pour 2 personnes</p>
            <ul className="text-sm space-y-2 pt-5 border-t border-white/20 text-white/85"><li>Arrivée vendredi · 16h</li><li>Départ dimanche · 12h</li><li>+15€ / voyageur supplémentaire</li></ul>
          </div>
        </div>
      </section>

      <div className="bg-refuge-bg py-10 text-center">
        <Link to="/accueil" data-testid="back-to-hub" className="inline-flex items-center gap-2 font-body text-xs tracking-[0.18em] uppercase text-refuge-soft hover:text-refuge-ruby transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.3} /> Retour à l'accueil
        </Link>
      </div>
    </div>
  );
}
